import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { operationalCostsApi } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';

const CATEGORIES = [
  { id: 'bahan_baku', label: 'Bahan Baku' },
  { id: 'listrik', label: 'Listrik' },
  { id: 'air', label: 'Air' },
  { id: 'sewa', label: 'Sewa Tempat' },
  { id: 'gaji', label: 'Gaji' },
  { id: 'peralatan', label: 'Peralatan' },
  { id: 'lain', label: 'Lain-lain' },
];

export default function AdminCostsPage() {
  const { t } = useLanguage();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '', category: 'lain', notes: '' });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const { data: costData, isLoading } = useQuery({
    queryKey: ['operational-costs', page],
    queryFn: () => operationalCostsApi.listPaginated({ page, limit: PAGE_SIZE }).then(r => r.data),
  });
  const costs = costData?.items || [];

  const addMutation = useMutation({
    mutationFn: () => operationalCostsApi.create({
      name: form.description,
      amount: parseFloat(form.amount),
      category: form.category,
      notes: form.notes,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-costs'] });
      toast.success('Biaya berhasil ditambahkan');
      setForm({ description: '', amount: '', category: 'lain', notes: '' });
      setShowForm(false);
    },
    onError: () => toast.error('Gagal menambahkan biaya'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => operationalCostsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operational-costs'] });
      toast.success('Biaya dihapus');
    },
    onError: () => toast.error('Gagal menghapus biaya'),
  });

  const totalMonth = costData?.month_total || 0;
  const totalPages = Math.ceil((costData?.total || 0) / PAGE_SIZE);

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Biaya Operasional', 'Operational Costs')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Catat pengeluaran operasional toko', 'Track store operational expenses')}</p>
          </div>
          <button onClick={() => setShowForm(true)} data-testid="add-cost-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={18} />{t('Tambah Biaya', 'Add Cost')}
          </button>
        </div>

        {/* Summary card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
              <Receipt size={22} className="text-red-500" />
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">{t('Total Pengeluaran Bulan Ini', 'This Month Expenses')}</p>
              <p className="text-2xl font-bold text-slate-900 font-manrope">{formatCurrency(totalMonth)}</p>
            </div>
          </div>
        </div>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
            <h3 className="font-semibold text-slate-800">{t('Tambah Biaya Baru', 'Add New Cost')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">{t('Keterangan *', 'Description *')}</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Contoh: Beli deterjen 10kg"
                  data-testid="cost-description-input"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">{t('Jumlah *', 'Amount *')}</label>
                <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0"
                  data-testid="cost-amount-input"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">{t('Kategori', 'Category')}</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50">
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">{t('Catatan', 'Notes')}</label>
                <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Opsional"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44] bg-slate-50" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200">
                {t('Batal', 'Cancel')}
              </button>
              <button onClick={() => addMutation.mutate()} disabled={addMutation.isPending || !form.description || !form.amount}
                data-testid="save-cost-btn"
                className="flex-1 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] disabled:opacity-60 transition-all flex items-center justify-center gap-1">
                {addMutation.isPending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : t('Simpan', 'Save')}
              </button>
            </div>
          </div>
        )}

        {/* Costs list */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
            </div>
          ) : costs.length === 0 ? (
            <div className="text-center py-12">
              <Receipt size={32} className="text-slate-200 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">{t('Belum ada pengeluaran', 'No expenses yet')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {costs.map((cost, i) => (
                <div key={cost.id || i} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Receipt size={15} className="text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{cost.name || cost.description}</p>
                      <p className="text-xs text-slate-400">{CATEGORIES.find(c => c.id === cost.category)?.label || cost.category} · {formatDate(cost.date || cost.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold text-red-600 text-sm">{formatCurrency(cost.amount)}</p>
                    <button onClick={() => deleteMutation.mutate(cost.id)} data-testid={`delete-cost-${cost.id}`}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
              <p className="text-sm text-slate-500">{t('Halaman', 'Page')} {page} / {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page === 1} data-testid="costs-previous-page-button" className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40 transition-colors">{t('Sebelumnya', 'Prev')}</button>
                <button onClick={() => setPage(current => Math.min(totalPages, current + 1))} disabled={page >= totalPages} data-testid="costs-next-page-button" className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 disabled:opacity-40 transition-colors">{t('Berikutnya', 'Next')}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
